import { Fragment, useState } from 'react';
import { useQuery } from '@apollo/client';
import MovieModel from 'models/movie.program.model';
import AllMoviesQuery from 'queries/programs/allMovies.query';
import { FIXED_COUNT, CLIENT, MOVIES } from '../../../constants';
import Ad from '../../Ad';
import ErrorBoundary from '../../ErrorBoundary';
import ErrorMessage from '../../ErrorMessage';
import PortraitItem from '../../PortraitItem';
import PortraitItemFallback from '../../PortraitItem/PortraitItemFallback';
import { SkeletonMovieSection } from '../../SkeletonElement/SkeletonElement';
import { Grid, GridElement } from '../../../styles/grid';
import { BodyText, Heading2 } from '../../Text/Text.style';
import { MoviesSectionWrapperStyle, MoviesAdStyle } from '../Movies.style';
import { TileListStyle } from '../../Lists/TileList/TileList.style';
import ProgramList from '../../Home/ProgramListSection/ProgramList';

const MovieSection = ({ viewMode }: { viewMode: string }): JSX.Element => {
  const [movies, setMovies] = useState<{ latest: MovieModel[]; comingUp: MovieModel[]; all: MovieModel[] }>({
    latest: [],
    comingUp: [],
    all: []
  });

  const { error, loading } = useQuery<{
    programs: { items: IProgramGraphql[]; totalResults: number };
  }>(AllMoviesQuery, {
    variables: {
      limit: FIXED_COUNT.ALL_MOVIES_LIMIT
    },
    onCompleted: data => {
      if (!data.programs?.items?.length) return;
      const latest: MovieModel[] = [];
      const comingUp: MovieModel[] = [];
      const all: MovieModel[] = [];
      data.programs.items.forEach(item => {
        const movie = new MovieModel(item);
        if (!movie.isAvailable) {
          return comingUp.push({
            ...movie,
            eventValues: {
              ...movie.eventValues,
              label: MOVIES.COMING_UP,
              value: comingUp.length + 1
            }
          });
        }
        if (latest.length < 10) {
          latest.push({
            ...movie,
            eventValues: {
              ...movie.eventValues,
              label: MOVIES.LATEST,
              value: latest.length + 1
            }
          });
        }
        all.push({
          ...movie,
          eventValues: {
            ...movie.eventValues,
            label: MOVIES.ALL,
            value: all.length + 1
          }
        });
      });

      setMovies({ latest, comingUp, all });
    }
  });

  if (loading) {
    return (
      <MoviesSectionWrapperStyle data-testid="movieSection">
        <Grid>
          <GridElement smColumn="1 / 13" mdColumn="1 / 13" lgColumn="1 / 13">
            <SkeletonMovieSection />
          </GridElement>
        </Grid>
      </MoviesSectionWrapperStyle>
    );
  }

  if (error) {
    return <ErrorMessage />;
  }

  return (
    <MoviesSectionWrapperStyle data-testid="movieSection">
      {!!movies.latest.length && (
        <>
          <Grid>
            <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
              <Heading2>{MOVIES.LATEST}</Heading2>
            </GridElement>
          </Grid>
          <ProgramList programs={movies.latest} sectionType="editorials" />
        </>
      )}
      {!!movies.comingUp.length && (
        <>
          <Grid>
            <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
              <Heading2>{MOVIES.COMING_UP}</Heading2>
            </GridElement>
          </Grid>
          <ProgramList programs={movies.comingUp} sectionType="editorials" />
        </>
      )}
      {!!movies.all.length && (
        <Grid>
          <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
            <Heading2>{MOVIES.ALL}</Heading2>
          </GridElement>
          <GridElement smColumn="1 / 13" mdColumn="1 / 13" lgColumn="1 / 13">
            <TileListStyle data-tesid="movieList">
              {movies.all.map(program => (
                <Fragment key={program.guid}>
                  <ErrorBoundary onError={(_: any) => <PortraitItemFallback />}>
                    <PortraitItem program={program} key={program.guid} />
                  </ErrorBoundary>
                </Fragment>
              ))}
            </TileListStyle>
            <MoviesAdStyle>
              <Ad id={viewMode === CLIENT.MOBILE ? 'm-r1' : 'h1'} page="movies" />
            </MoviesAdStyle>
          </GridElement>
        </Grid>
      )}
      {!movies.comingUp.length && !movies.latest.length && (
        <Grid>
          <GridElement lgColumn="1 / 13" mdColumn="1 / 13" smColumn="1 / 13">
            <BodyText>Helaas, op dit moment zijn er geen films beschikbaar op KIJK.</BodyText>
          </GridElement>
        </Grid>
      )}
    </MoviesSectionWrapperStyle>
  );
};

export default MovieSection;
